import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  Box,
  Container,
  Typography,
  Alert,
  Stack,
  Grid,
  Link,
} from "@mui/material";
import { useExplorerDates } from "./Explorer/useExplorerDates";
import { useAuth } from "../contexts/AuthContext";
import DayRangePicker from "../components/DayRangePicker";
import ExplorerExportButton from "./Explorer/ExplorerExportButton";
import type { ExportConfig } from "../export";
import {
  entityExportReports,
  exportAlternatives,
} from "./Explorer/explorerTabs";
import { TrendChartCard } from "./Explorer/TrendChartCard";
import { SummaryCard } from "./Explorer/SummarySection";
import { calculateMedian, explorerAccentAt } from "./Explorer/utils";
import { ExplorerHeader } from "./Explorer/ExplorerHeader";
import { ExplorerBrandsSection } from "./Explorer/ExplorerBrandsSection";
import { ProgramDetailRecentEpisodesTable } from "./Explorer/ProgramDetailRecentEpisodesTable";
import apiService from "../services/api";
import { resolveProgramCoverUrl } from "../utils/imageUtils";

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const ExplorerProgramDetail: React.FC = () => {
  const { t } = useTranslation("explorer");
const { programId } = useParams<{ programId: string }>();
  const navigate = useNavigate();

  const { getDateFilterMinStartDate, getDateFilterMaxEndDate, hasExplorerTabAccess } = useAuth();
  const { startDate, endDate, setRange } = useExplorerDates();
  const currentMinDate = getDateFilterMinStartDate();
  const currentMaxDate = getDateFilterMaxEndDate();

  const { data, isLoading, isFetching, isPlaceholderData, error } = useQuery({
    queryKey: ["explorer-program-detail", programId, startDate, endDate],
    queryFn: () =>
      apiService.getExplorerProgramDetail(programId!, startDate, endDate),
    enabled: !!programId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { data: trendsData, isFetching: trendsFetching, isPlaceholderData: trendsIsPlaceholderData } = useQuery({
    queryKey: ["explorer-program-trends", programId, startDate, endDate],
    queryFn: () =>
      apiService.getExplorerProgramTrends(programId!, startDate, endDate),
    enabled: !!programId,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fetchBrandMentions = useCallback(
    (brandId: string) =>
      apiService.getExplorerProgramBrandMentions(
        programId!,
        brandId,
        startDate,
        endDate,
        "all",
      ),
    [programId, startDate, endDate],
  );

  const exportReports = entityExportReports(hasExplorerTabAccess, "program").join(",");
  const exportPodcastId = data?.program?.podcast?._id;
  const exportProgramName = data?.program?.name;
  const exportConfig = useMemo<ExportConfig | undefined>(() => {
    const reports = exportReports ? exportReports.split(",") : [];
    if (!programId || !exportPodcastId || reports.length === 0) return undefined;
    return {
      reportType: reports[0]!,
      params: { startDate, endDate },
      contextLabels: exportProgramName ? [{ label: t("program"), value: exportProgramName }] : [],
      initialFilters: { podcastIds: [exportPodcastId], programIds: [programId] },
      alternatives: exportAlternatives(reports),
    };
  }, [programId, exportPodcastId, exportProgramName, startDate, endDate, exportReports, t]);

  const showDetailSkeleton =
    (isLoading && !data) || (isFetching && isPlaceholderData);
  const showTrendsSkeleton =
    trendsFetching && (!trendsData || trendsIsPlaceholderData);

  if (error && !data) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Alert severity="error">{t("loadProgramError")}</Alert>
      </Container>
    );
  }

  const program = data?.program;
  const playlistId = program?.youtubePlaylistIds?.find((id) => id.trim().length > 0);
  const playlistUrl = playlistId
    ? `https://www.youtube.com/playlist?list=${encodeURIComponent(playlistId.trim())}`
    : null;
  const brands = data?.brands ?? [];
  const episodeCount = data?.episodeStats?.count ?? 0;

  const trends = trendsData?.trends ?? [];
  const hasMentionData = trends.some((t) => (t.mentionsContent || 0) > 0);

  const totalViews = data?.episodeStats?.totalViews ?? 0;
  const avgViewsPerEp =
    episodeCount > 0 ? Math.round(totalViews / episodeCount) : 0;

  return (
    <Container maxWidth="xl" sx={{ pb: 4 }}>
      <ExplorerHeader
        loading={showDetailSkeleton}
        onBack={() => navigate(-1)}
        image={{
          name: program?.name ?? "",
          src: program
            ? resolveProgramCoverUrl({
                program,
                podcast: program.podcast ?? undefined,
              })
            : undefined,
        }}
        title={program?.name ?? ""}
        subtitle={program?.podcast?.title ?? ""}
        subtitleExtra={
          playlistUrl ? (
            <Link
              href={playlistUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="always"
              variant="body2"
              color="text.primary"
              noWrap
            >
              {t("goToPlaylist")}
            </Link>
          ) : null
        }
      >
        <DayRangePicker
          startDate={startDate}
          endDate={endDate}
          onChange={setRange}
          minDate={currentMinDate}
          maxDate={currentMaxDate}
        />
        <ExplorerExportButton config={exportConfig} />
      </ExplorerHeader>

      {/* Summary Stats */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          {t("periodSummary")}
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="stretch"
        >
          <SummaryCard
            title={t("episodes")}
            value={episodeCount}
            color={explorerAccentAt(0)}
            isLoading={showDetailSkeleton}
          />
          <SummaryCard
            title={t("viewsTotal")}
            value={formatNumber(totalViews)}
            color={explorerAccentAt(1)}
            isLoading={showDetailSkeleton}
          />
          <SummaryCard
            title={t("avgViewsPerEp")}
            value={formatNumber(avgViewsPerEp)}
            color={explorerAccentAt(2)}
            isLoading={showDetailSkeleton}
          />
        </Stack>
      </Box>

      {/* Weekly Trends Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h2" sx={{ mb: 2 }}>
          {t("weeklyTrends")}
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TrendChartCard
            title={t("rankingViewsPosition")}
            dataKey="position"
            color={explorerAccentAt(0)}
            tooltipLabel={t("position")}
            granularityTooltip={t("granularity.week")}
            data={trendsData?.trends || []}
            median={
              trendsData?.trends
                ? calculateMedian(
                    trendsData.trends
                      .map((t) => t.position)
                      .filter((v): v is number => typeof v === "number"),
                  )
                : undefined
            }
            formatValue={(v) => `#${Math.round(v)}`}
            allowDecimals={false}
            reversed={true}
            isLoading={showTrendsSkeleton}
          />
          <TrendChartCard
            title={t("views")}
            dataKey="views"
            color={explorerAccentAt(1)}
            tooltipLabel={t("views")}
            granularityTooltip={t("granularity.week")}
            data={trendsData?.trends || []}
            median={
              trendsData?.trends
                ? calculateMedian(
                    trendsData.trends
                      .map((t) => t.views)
                      .filter((v): v is number => typeof v === "number"),
                  )
                : undefined
            }
            formatValue={formatNumber}
            yAxisWidth={45}
            isLoading={showTrendsSkeleton}
          />
          {(showTrendsSkeleton || hasMentionData) && (
            <TrendChartCard
              title={t("contentMentions")}
              dataKey="mentionsContent"
              color={explorerAccentAt(2)}
              tooltipLabel={t("contentMentions")}
              granularityTooltip={t("granularity.week")}
              data={trends}
              median={
                trends.length > 0 && !showTrendsSkeleton
                  ? calculateMedian(
                      trends
                        .map((t) => t.mentionsContent)
                        .filter(
                          (v): v is number =>
                            typeof v === "number" && v > 0,
                        ),
                    )
                  : undefined
              }
              allowDecimals={false}
              isLoading={showTrendsSkeleton}
            />
          )}
        </Stack>
      </Box>

      <Grid container spacing={4}>
        {/* Brands Section */}
        <Grid size={{ xs: 12 }}>
          <ExplorerBrandsSection
            brands={brands}
            isLoading={showDetailSkeleton}
            isFetching={isFetching}
            mentionsQueryKey={[
              "explorer-program-brand-mentions",
              programId,
              startDate,
              endDate,
              "all",
            ]}
            fetchMentions={fetchBrandMentions}
          />
        </Grid>

        {/* Episodes Section */}
        <Grid size={{ xs: 12 }}>
          <ProgramDetailRecentEpisodesTable
            programId={programId!}
            podcastId={program?.podcast?._id}
            startDate={startDate}
            endDate={endDate}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default ExplorerProgramDetail;
